import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MessageCircle, Phone, Check, Clock, Shield, Award, Battery, Zap, ThermometerSun } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Layout from '@/components/layout/Layout';
import BookingModal from '@/components/BookingModal';
import JsonLd, { generateProductSchema, generateBreadcrumbSchema } from '@/components/seo/JsonLd';
import FAQSection from '@/components/FAQSection';
import SEOHead from '@/components/seo/SEOHead';
import PageBreadcrumb from '@/components/PageBreadcrumb';
import OptimizedImage from '@/components/ui/OptimizedImage';
import iphoneImage from '@/assets/iphone-repair.jpg';

const batteryReplacementSchema = generateProductSchema({
  name: 'iPhone Battery Replacement Service in Patna, Bihar',
  description: 'Professional iPhone battery replacement in Patna. Same-day service for all iPhone models. Fix battery drain, overheating, and swelling issues. High-capacity batteries with warranty.',
  url: 'https://ifixappleservicecenter.in/iphone-battery-replacement',
  priceRange: '₹1,499 - ₹5,999',
  ratingValue: 4.8,
  reviewCount: 380,
});

const batteryBreadcrumbSchema = generateBreadcrumbSchema([
  { name: 'Home', url: 'https://ifixappleservicecenter.in/' },
  { name: 'iPhone Service', url: 'https://ifixappleservicecenter.in/iphone-service' },
  { name: 'Battery Replacement', url: 'https://ifixappleservicecenter.in/iphone-battery-replacement' },
]);

const batteryFaqs = [
  {
    question: 'How do I know if my iPhone needs a battery replacement?',
    answer: 'Signs include: battery draining quickly, phone shutting down unexpectedly, battery health below 80%, phone overheating, slow performance, or swollen battery causing screen to lift. Check Settings > Battery > Battery Health for your current capacity.',
  },
  {
    question: 'How long does iPhone battery replacement take?',
    answer: 'iPhone battery replacement takes approximately 20-45 minutes at our Patna service center. We use professional tools and techniques to ensure safe removal and installation of your new battery.',
  },
  {
    question: 'What is the cost of iPhone battery replacement in Patna?',
    answer: 'Battery replacement costs vary by iPhone model. Contact us at +91 97986 53591 or WhatsApp for an accurate quote for your specific model. All replacements include a 6-month warranty.',
  },
  {
    question: 'Do you use original Apple batteries?',
    answer: 'We use premium high-capacity batteries that meet or exceed Apple\'s specifications. All batteries undergo quality testing and come with a 6-month warranty covering defects and capacity retention.',
  },
  {
    question: 'Will battery replacement affect my data?',
    answer: 'No, battery replacement does not affect your data. However, we recommend backing up your iPhone before any repair as a precaution. Your photos, apps, and settings will remain intact.',
  },
  {
    question: 'How long will the new battery last?',
    answer: 'A new battery typically maintains 80% capacity for 500 complete charge cycles, which is about 2-3 years of normal use. Proper charging habits can extend battery life further.',
  },
];

const IPhoneBatteryReplacement = () => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  const benefits = [
    {
      icon: Clock,
      title: '20-45 Minute Service',
      description: 'Quick battery replacement while you wait',
    },
    {
      icon: Shield,
      title: '6-Month Warranty',
      description: 'Extended warranty on all battery replacements',
    },
    {
      icon: Battery,
      title: 'High-Capacity Cells',
      description: 'Premium batteries matching Apple specs',
    },
    {
      icon: Zap,
      title: 'Restore Performance',
      description: 'Fix slowdowns caused by degraded battery',
    },
  ];

  const batteryIssues = [
    {
      icon: Battery,
      title: 'Fast Battery Drain',
      description: 'Battery depleting quickly even with minimal use',
    },
    {
      icon: ThermometerSun,
      title: 'Overheating',
      description: 'Phone getting unusually hot during normal use',
    },
    {
      icon: Zap,
      title: 'Unexpected Shutdowns',
      description: 'Phone turning off even with battery remaining',
    },
  ];


  const healthTips = [
    'Avoid extreme temperatures (below 0°C or above 35°C)',
    'Don\'t let battery drain to 0% regularly',
    'Use Apple-certified chargers and cables',
    'Enable Optimized Battery Charging in Settings',
    'Reduce screen brightness and background app refresh',
    'Update to the latest iOS for battery optimizations',
  ];

  return (
    <Layout>
      <SEOHead
        title="iPhone Battery Replacement Patna | Apple iPhone Service Center Near Me - iFix"
        description="Expert iPhone battery replacement at our Apple iPhone service center in Patna, Bihar. Fix battery drain, overheating & shutdowns. iPhone service center near me with same-day service for iPhone 6-16 Pro Max. 6-month warranty. Starts ₹1,499!"
        url="https://ifixappleservicecenter.in/iphone-service/battery-replacement"
      />
      <JsonLd data={batteryReplacementSchema} />
      <JsonLd data={batteryBreadcrumbSchema} />

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <OptimizedImage
            src={iphoneImage}
            alt="iPhone battery replacement service in Patna Bihar - fix battery drain overheating fast charging"
            className="w-full h-full object-cover"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/60" />
        </div>
        
        <div className="container-apple relative z-10 py-20">
          <PageBreadcrumb 
            items={[
              { name: 'iPhone Service', path: '/iphone-service' },
              { name: 'Battery Replacement', path: '/iphone-service/battery-replacement' },
            ]} 
            className="mb-8"
          />
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-foreground leading-tight mb-6 opacity-0 animate-fade-in">
            iPhone Battery Replacement
            <span className="block text-primary">in Patna, Bihar</span>
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl leading-relaxed opacity-0 animate-fade-in animation-delay-100">
            Is your iPhone battery draining fast or overheating? Looking for iPhone battery replacement near me? Get professional battery replacement at our Apple iPhone service center in Patna. 
            Same-day iPhone service with 6-month warranty at Bihar's trusted Apple service center in Patna.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-in animation-delay-200">
            <Button variant="apple-blue" size="lg" onClick={() => setIsBookingOpen(true)}>
              <Calendar className="w-5 h-5" />
              Book Battery Replacement
            </Button>
            <Button variant="whatsapp" size="lg" asChild>
              <a
                href="https://wa.me/919798653591?text=Hi,%20I%20need%20iPhone%20battery%20replacement"
                target="_blank"
                rel="noopener noreferrer"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp
              </a>
            </Button>
            <Button variant="call" size="lg" asChild>
              <a href="tel:9798653591">
                <Phone className="w-5 h-5" />
                Call Now
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding">
        <div className="container-apple">
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 text-center">
            Why Choose iFix for Battery Replacement?
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            Restore your iPhone's battery life with professional replacement service.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={benefit.title}
                className="p-6 bg-card rounded-2xl border border-border hover:border-primary/30 hover:shadow-apple-sm transition-all opacity-0 animate-fade-in text-center"
                style={{ animationDelay: `${index * 100}ms`, animationFillMode: 'forwards' }}
              >
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                  <benefit.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">{benefit.title}</h3>
                <p className="text-sm text-muted-foreground">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Battery Issues */}
      <section className="section-padding bg-secondary">
        <div className="container-apple">
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 text-center">
            Signs You Need Battery Replacement
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            Don't ignore these warning signs - a degraded battery affects your iPhone's performance.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto mb-12">
            {batteryIssues.map((issue, index) => (
              <div
                key={issue.title}
                className="p-8 bg-background rounded-2xl border border-border text-center opacity-0 animate-fade-in"
                style={{ animationDelay: `${index * 100}ms`, animationFillMode: 'forwards' }}
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-destructive/10 flex items-center justify-center">
                  <issue.icon className="w-8 h-8 text-destructive" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">{issue.title}</h3>
                <p className="text-muted-foreground">{issue.description}</p>
              </div>
            ))}
          </div>

          <div className="bg-background rounded-2xl p-8 border border-border max-w-3xl mx-auto">
            <h3 className="text-xl font-semibold text-foreground mb-4 text-center">Check Your Battery Health</h3>
            <p className="text-muted-foreground text-center mb-4">
              Go to <span className="text-primary font-medium">Settings → Battery → Battery Health & Charging</span>
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="px-4 py-2 bg-green-500/10 rounded-full text-green-600 dark:text-green-400 font-medium">
                80-100%: Good
              </div>
              <div className="px-4 py-2 bg-yellow-500/10 rounded-full text-yellow-600 dark:text-yellow-400 font-medium">
                70-79%: Consider Replacing
              </div>
              <div className="px-4 py-2 bg-destructive/10 rounded-full text-destructive font-medium">
                Below 70%: Replace Now
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Process Section */}
      <section className="section-padding bg-secondary">
        <div className="container-apple">
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 text-center">
            Our Battery Replacement Process
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            Safe and professional battery replacement ensuring your iPhone works like new.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              { step: '1', title: 'Health Check', description: 'Diagnose battery capacity and overall health' },
              { step: '2', title: 'Safe Removal', description: 'Carefully remove old battery using proper tools' },
              { step: '3', title: 'Installation', description: 'Install new high-capacity battery with adhesive' },
              { step: '4', title: 'Calibration', description: 'Test and calibrate for accurate readings' },
            ].map((item, index) => (
              <div key={item.step} className="text-center opacity-0 animate-fade-in" style={{ animationDelay: `${index * 100}ms`, animationFillMode: 'forwards' }}>
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold">
                  {item.step}
                </div>
                <h3 className="font-semibold text-foreground mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Battery Tips */}
      <section className="section-padding">
        <div className="container-apple">
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 text-center">
            Tips to Extend Battery Life
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            Follow these tips to maximize your new battery's lifespan.
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {healthTips.map((tip, index) => (
              <div
                key={tip}
                className="flex items-center gap-3 p-4 bg-card rounded-2xl border border-border opacity-0 animate-fade-in"
                style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'forwards' }}
              >
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-primary" />
                </div>
                <span className="text-sm font-medium text-foreground">{tip}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection
        title="Battery Replacement FAQs"
        subtitle="Common questions about iPhone battery replacement in Patna"
        faqs={batteryFaqs}
      />

      {/* Related Services */}
      <section className="section-padding bg-secondary">
        <div className="container-apple text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-8">
            Other iPhone Services
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/iphone-service/screen-replacement"
              className="px-6 py-3 bg-background rounded-full text-foreground font-medium border border-border hover:border-primary/30 transition-all"
            >
              Screen Replacement
            </Link>
            <Link
              to="/iphone-service"
              className="px-6 py-3 bg-background rounded-full text-foreground font-medium border border-border hover:border-primary/30 transition-all"
            >
              All iPhone Services
            </Link>
            <Link
              to="/blog/iphone-battery-draining-fast-fix-patna"
              className="px-6 py-3 bg-background rounded-full text-foreground font-medium border border-border hover:border-primary/30 transition-all"
            >
              Battery Drain Guide
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-foreground">
        <div className="container-apple text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-background mb-4">
            Restore Your iPhone's Battery Life
          </h2>
          <p className="text-background/70 mb-8 max-w-xl mx-auto">
            Stop worrying about battery percentage. Get professional battery replacement with 6-month warranty.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="apple-blue" size="lg" onClick={() => setIsBookingOpen(true)}>
              <Calendar className="w-5 h-5" />
              Book Battery Replacement
            </Button>
            <Button variant="call" size="lg" asChild>
              <a href="tel:9798653591">
                <Phone className="w-5 h-5" />
                Call Now
              </a>
            </Button>
          </div>
        </div>
      </section>

      <BookingModal isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} />
    </Layout>
  );
};

export default IPhoneBatteryReplacement;
